import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useSpring, useInView, useMotionValue, useTransform, animate } from "framer-motion";

/* ==============================================================
 * ScrollProgress — 1px gold hairline at the very top of the page
 * tracking the document scroll position.
 * ============================================================== */
export function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.3 });
  return (
    <motion.div
      style={{ scaleX }}
      className="fixed top-0 left-0 right-0 h-[2px] bg-gold z-[100] origin-left"
      data-testid="scroll-progress"
      aria-hidden="true"
    />
  );
}

/* ==============================================================
 * Marquee — infinite horizontal editorial ticker strip.
 * Pure CSS keyframe animation. Duplicates children so the loop
 * is seamless. Accepts either `items` array or `children`.
 * ============================================================== */
export function Marquee({ items, children, speed = 40, className = "" }) {
  // Support both items prop (array of strings) and children (JSX)
  const useItems = items && items.length > 0;

  return (
    <div
      className={`relative overflow-hidden ${className}`}
      data-testid="marquee"
      aria-hidden="true"
    >
      <div
        className="flex whitespace-nowrap will-change-transform"
        style={{ animation: `marquee ${speed}s linear infinite` }}
      >
        {useItems ? (
          // Legacy items prop support
          [0, 1].map((loop) => (
            <div key={loop} className="flex items-center shrink-0">
              {items.map((item, i) => (
                <React.Fragment key={`${loop}-${i}`}>
                  <span className="text-[0.7rem] tracking-[0.32em] uppercase font-medium text-bone/85 px-10">
                    {item}
                  </span>
                  <span className="text-gold text-base leading-none" aria-hidden="true">◆</span>
                </React.Fragment>
              ))}
            </div>
          ))
        ) : (
          // Children support - duplicate for seamless loop
          [0, 1].map((loop) => (
            <div key={loop} className="flex items-center shrink-0">
              {children}
            </div>
          ))
        )}
      </div>
      <style>{`
        @keyframes marquee {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}

/* ==============================================================
 * CountUp — animates a number from 0 → target when scrolled into view.
 * Supports numeric formatting (commas, decimals).
 * ============================================================== */
export function CountUp({ to, duration = 1.8, decimals = 0, format, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const motionValue = useMotionValue(0);
  const rounded = useTransform(motionValue, (latest) => {
    const v = latest;
    if (format) return format(v);
    return decimals > 0 ? v.toFixed(decimals) : Math.round(v).toLocaleString();
  });
  const [display, setDisplay] = useState(decimals > 0 ? Number(0).toFixed(decimals) : "0");

  useEffect(() => {
    if (!inView) return;
    const controls = animate(motionValue, to, { duration, ease: [0.22, 1, 0.36, 1] });
    const unsub = rounded.on("change", (v) => setDisplay(v));
    return () => {
      controls.stop();
      unsub();
    };
  }, [inView, to, duration, motionValue, rounded]);

  // Safety net: if the in-view observer never fires (some mobile browsers /
  // odd layouts), snap to the final value rather than sitting at "0" forever.
  useEffect(() => {
    const t = setTimeout(() => {
      if (motionValue.get() === 0 && to !== 0) {
        const v = format ? format(to)
          : decimals > 0 ? Number(to).toFixed(decimals) : Math.round(to).toLocaleString();
        setDisplay(v);
        motionValue.set(to);
      }
    }, 2500);
    return () => clearTimeout(t);
  }, [to, decimals, format, motionValue]);

  return <span ref={ref} className={className} data-testid="count-up">{display}</span>;
}

/* ==============================================================
 * RevealHeading — splits children by <br/> into lines. Each line
 * animates as a single rise + fade unit. Works with inline italic
 * spans, punctuation, and mixed children without layout quirks.
 * ============================================================== */
export function RevealHeading({ children, delay = 0, stagger = 0.12, className = "", as: Tag = "h1" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10%" });

  // Partition children into lines, breaking on <br/>.
  const lines = [[]];
  React.Children.forEach(children, (child) => {
    if (child == null || child === false) return;
    if (React.isValidElement(child) && child.type === "br") {
      lines.push([]);
      return;
    }
    lines[lines.length - 1].push(child);
  });

  return (
    <Tag ref={ref} className={className} data-testid="reveal-heading">
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden">
          <motion.span
            className="inline-block"
            initial={{ y: "110%", opacity: 0 }}
            animate={inView ? { y: 0, opacity: 1 } : { y: "110%", opacity: 0 }}
            transition={{
              duration: 1.1,
              delay: delay + i * stagger,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </Tag>
  );
}

/* ==============================================================
 * MagneticButton — primary CTA that subtly pulls toward cursor on
 * hover (luxury micro-interaction, disabled on touch).
 * ============================================================== */
export function MagneticButton({ children, strength = 0.25, as = "button", ...rest }) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 150, damping: 15, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 150, damping: 15, mass: 0.4 });

  const onMove = (e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    x.set((e.clientX - cx) * strength);
    y.set((e.clientY - cy) * strength);
  };
  const onLeave = () => {
    x.set(0);
    y.set(0);
  };

  const Component = motion[as] ?? motion.button;
  return (
    <Component
      ref={ref}
      style={{ x: sx, y: sy }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      {...rest}
    >
      {children}
    </Component>
  );
}

/* ==============================================================
 * SectionNumber — "01 — Label" editorial chapter marker.
 * ============================================================== */
export function SectionNumber({ n, label, tone = "muted" }) {
  const toneCls =
    tone === "gold" ? "text-gold"
    : tone === "bone" ? "text-bone/75"
    : "text-ink/55";
  return (
    <div className={`flex items-center gap-3 ${toneCls}`} data-testid="section-number">
      <span className="font-display italic text-[1.15rem] leading-none">
        {String(n).padStart(2, "0")}
      </span>
      <span className="h-px w-8 bg-current opacity-40" />
      <span className="text-[0.68rem] tracking-[0.24em] uppercase font-medium">{label}</span>
    </div>
  );
}

/* ==============================================================
 * PullQuote — editorial testimonial block with large gold quote.
 * ============================================================== */
export function PullQuote({ quote, attribution, className = "" }) {
  return (
    <figure className={`relative ${className}`} data-testid="pull-quote">
      <div
        className="absolute -top-12 -left-2 md:-left-8 font-display text-gold/40 leading-none select-none pointer-events-none"
        style={{ fontSize: "11rem", lineHeight: 0.8 }}
        aria-hidden="true"
      >
        &ldquo;
      </div>
      <blockquote className="relative font-display italic text-[clamp(1.75rem,3.2vw,2.75rem)] leading-[1.22] tracking-tighter">
        {quote}
      </blockquote>
      {attribution && (
        <figcaption className="mt-6 flex items-center gap-3 text-[0.72rem] tracking-caps uppercase font-medium">
          <span className="h-px w-10 bg-current opacity-40" />
          <span>{attribution}</span>
        </figcaption>
      )}
    </figure>
  );
}
